import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RotateCcw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { api } from '../../context/AuthContext';
import { DataTable, Loading, Section, errorText, formatDateTime } from './shared';

const unitOf = (price) => (price.credits_per_second != null ? 'per second' : price.per ? `per ${price.per}` : 'per action');
const fieldOf = (price) => (price.credits_per_second != null ? 'credits_per_second' : 'credits');

const PriceEditor = ({ feature, price, onChanged }) => {
  const field = fieldOf(price);
  const [value, setValue] = useState(price[field]);
  useEffect(() => setValue(price[field]), [price, field]);
  const changed = Number(value) !== price[field];

  const save = async () => {
    try {
      await api.put(`/admin/ai/credit-prices/${feature}`, { credits: Number(value) });
      toast.success(`${price.label}: ${value} credit${Number(value) === 1 ? '' : 's'} ${unitOf(price)}`);
      onChanged();
    } catch (error) {
      toast.error(errorText(error, 'Could not save price'));
    }
  };
  const reset = async () => {
    await api.delete(`/admin/ai/credit-prices/${feature}`);
    toast.success(`${price.label} reset to default`);
    onChanged();
  };

  return (
    <div className="flex items-center justify-end gap-2">
      <Input type="number" min={0} max={1000} value={value} onChange={(e) => setValue(e.target.value)}
        className="h-8 w-20 text-right tabular-nums" aria-label={`${price.label} credits`} />
      <Button size="sm" variant={changed ? 'default' : 'outline'} disabled={!changed} onClick={save}
        className={changed ? 'h-8 bg-emerald-500 text-white hover:bg-emerald-600' : 'h-8'}>Save</Button>
      <Button size="icon" variant="ghost" className="h-8 w-8" disabled={!price.overridden} onClick={reset}
        aria-label={`Reset ${price.label}`} title="Reset to default">
        <RotateCcw className="h-4 w-4" />
      </Button>
    </div>
  );
};

/** Credit price list editor and account credit support (lookup, balance, adjust). */
const CreditsAdminTab = () => {
  const [prices, setPrices] = useState(null);
  const [email, setEmail] = useState('');
  const [account, setAccount] = useState(null);
  const [adjust, setAdjust] = useState({ amount: '', note: '' });
  const [busy, setBusy] = useState(false);

  const loadPrices = useCallback(() => api.get('/admin/ai/credit-prices').then((res) => setPrices(res.data.prices)), []);
  useEffect(() => { loadPrices().catch(() => setPrices({})); }, [loadPrices]);

  const lookup = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const user = (await api.get('/admin/ai/accounts', { params: { email } })).data;
      const credits = (await api.get(`/admin/ai/credits/${user.user_id}`, { params: { limit: 15 } })).data;
      setAccount({ user, ...credits });
    } catch (error) {
      setAccount(null);
      toast.error(errorText(error, 'Lookup failed'));
    } finally {
      setBusy(false);
    }
  };

  const submitAdjust = async () => {
    try {
      const res = await api.post('/admin/ai/credits/adjust', {
        user_id: account.user.user_id, amount: Number(adjust.amount), note: adjust.note,
      });
      toast.success(`Done. New balance: ${res.data.balance.toLocaleString()}`);
      setAdjust({ amount: '', note: '' });
      lookup();
    } catch (error) {
      toast.error(errorText(error, 'Adjustment failed'));
    }
  };

  if (!prices) return <Loading />;
  const rows = Object.entries(prices).map(([feature, price]) => ({ feature, ...price }));

  return (
    <div className="space-y-6">
      <Section title="Credit prices"
        description="What each AI action costs. Changes apply to new actions within a minute and are recorded in the audit log. Plan allowances and Stripe prices stay in code.">
        <DataTable
          rowKey={(r) => r.feature}
          rows={rows}
          columns={[
            { key: 'label', label: 'AI action', render: (r) => (
              <div>
                <p className="font-medium text-slate-800">{r.label}</p>
                <p className="text-xs text-slate-400"><code>{r.feature}</code></p>
              </div>
            ) },
            { key: 'unit', label: 'Unit', className: 'text-slate-600', render: unitOf },
            { key: 'default', label: 'Default', align: 'right', className: 'text-slate-500',
              render: (r) => (
                <span className="inline-flex items-center gap-2">
                  {r.overridden && <Badge className="bg-indigo-50 font-normal text-indigo-700 hover:bg-indigo-50">edited</Badge>}
                  {r.default[fieldOf(r)]}
                </span>
              ) },
            { key: 'credits', label: 'Credits', align: 'right', className: 'w-64',
              render: (r) => <PriceEditor feature={r.feature} price={prices[r.feature]} onChanged={loadPrices} /> },
          ]}
        />
      </Section>

      <Section title="Account credits"
        description="Look up a user to see their billing account's balance and ledger, or grant and remove credits.">
        <form onSubmit={lookup} className="flex max-w-md gap-2">
          <Input type="email" placeholder="user@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
            aria-label="User email" />
          <Button type="submit" variant="outline" disabled={!email || busy} aria-label="Look up">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </form>

        {account && (
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500">Account</p>
                <p className="mt-1 truncate font-medium text-slate-900">{account.user.name || account.user.email}</p>
                <p className="truncate text-sm text-slate-500">{account.user.email}</p>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500">Plan</p>
                <p className="mt-1 font-medium capitalize text-slate-900">{(account.user.plan_id || 'free_trial').replace('_', ' ')}</p>
                {account.balance.is_team_pool && <p className="text-sm text-slate-500">Uses {account.balance.account_id}&apos;s team pool</p>}
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500">Balance</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
                  {account.balance.unlimited ? 'Unlimited' : account.balance.balance.toLocaleString()}
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-slate-50 p-4">
              <p className="mb-3 text-sm font-medium text-slate-700">Adjust credits</p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input type="number" placeholder="+100 or -50" value={adjust.amount} className="sm:w-36"
                  onChange={(e) => setAdjust({ ...adjust, amount: e.target.value })} aria-label="Amount" />
                <Input placeholder="Reason (required, kept in the ledger)" value={adjust.note} className="sm:flex-1"
                  onChange={(e) => setAdjust({ ...adjust, note: e.target.value })} aria-label="Reason" />
                <Button variant="outline" disabled={!Number(adjust.amount) || !adjust.note.trim()} onClick={submitAdjust}>Apply</Button>
              </div>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-slate-700">Recent ledger</p>
              <DataTable
                rowKey={(r) => r.entry_id}
                rows={account.ledger}
                empty="No credit activity"
                columns={[
                  { key: 'created_at', label: 'When', className: 'whitespace-nowrap text-slate-500', render: (r) => formatDateTime(r.created_at) },
                  { key: 'type', label: 'Type', className: 'capitalize' },
                  { key: 'detail', label: 'Detail', className: 'text-slate-600',
                    render: (r) => [r.feature || r.source, r.note].filter(Boolean).join(' · ') || '-' },
                  { key: 'amount', label: 'Credits', align: 'right',
                    render: (r) => <span className={r.amount > 0 ? 'text-emerald-700' : 'text-slate-700'}>{r.amount > 0 ? '+' : ''}{r.amount.toLocaleString()}</span> },
                ]}
              />
            </div>
          </div>
        )}
      </Section>
    </div>
  );
};

export default CreditsAdminTab;
